import React, {useEffect, useState} from 'react';
import {useTranslate} from '../../../hooks';
import {
  formatDate,
  useCountry,
  useFlexTransactions,
} from '@resurs-internal/rb-app-api';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {BrandIcon, IconBadge} from '../../../components';
import {PostponeOfferDialogRoutes} from './PostponeOfferDialog';
import {BaseList} from '@resurs-internal/mui';

export const PostponeOfferDialogUpdate: React.FC<{
  navigate: (key: PostponeOfferDialogRoutes) => void;
}> = ({navigate}) => {
  const translate = useTranslate();
  const country = useCountry();
  const flex = useFlexTransactions('postpone');
  const [enabled, setEnabled] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const handleCheckChange = (id: string) => () => {
    setEnabled(true);
    setChecked((prev) => ({...prev, [id]: !prev[id]}));
  };

  // initialise checked state
  useEffect(() => {
    if (!flex.query.data) return;
    const initial: Record<string, boolean> = {};
    const checked = flex.query.data?.reduce((result, e) => {
      result[e.id] = e?.postponementStatus?.applied ?? false;
      return result;
    }, initial);

    setChecked(checked);
  }, [flex.query.data]);

  const onSave = async () => {
    // update all checked
    await flex.updateAll.mutateAsync(
      flex.query.data?.map((e) => ({
        id: e.id,
        postpone: Boolean(checked[e.id]),
        markAsDownPayment: false,
      })),
    );
    navigate('success');
  };

  return (
    <>
      <DialogContent sx={{padding: 0}}>
        <Stack spacing={2} alignItems={'center'} p={3}>
          <BrandIcon chainId={'2570'} size={'xl'} />
          <Typography typography={'h4'} textAlign={'center'}>
            {translate('postpone.offer.subtitle')}
          </Typography>
          <Typography typography={'body1'} sx={{width: '100%'}}>
            {translate('postpone.offer.description', [
              country.formatDate(flex.dueDate),
            ])}
          </Typography>
          <BaseList
            sx={{width: '100%'}}
            items={flex.query.data?.map((e) => ({
              id: e.id,
              title:
                e.merchantName ??
                e.merchantLocation ??
                e.transactionTypeText ??
                '',
              subtitle: formatDate(e.transactionDate),
              icon: (
                <Checkbox
                  checked={Boolean(checked[e.id])}
                  onChange={handleCheckChange(e.id)}
                />
              ),
              endAdornment: (
                <Typography sx={{fontWeight: 'bold', mr: 1}}>
                  {country.formatAmount(e.amount)}
                </Typography>
              ),
            }))}
          />
        </Stack>
        <Box
          sx={(theme) => ({
            p: 3,
            background: theme.palette.background.paper,
          })}>
          <Stack spacing={2}>
            <IconBadge type={'font'} icon={'scale-balanced'} inactive={true} />
            <Typography typography={'subtitle2'}>
              {translate('flex.offer.terms.title')}
            </Typography>
            <Typography typography={'caption'}>
              {translate('flex.offer.terms.text')}
            </Typography>
            <Typography typography={'subtitle2'}>
              {translate('flex.offer.undo.title')}
            </Typography>
            <Typography typography={'caption'}>
              {translate('flex.offer.undo.text')}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button
          sx={(t) => ({color: t.palette.text.secondary})}
          color={'secondary'}
          variant={'text'}
          onClick={() => navigate('close')}>
          {translate('common.cancel')}
        </Button>
        <Button variant={'text'} disabled={!enabled} onClick={onSave}>
          {translate('flex.offer.activate')}
        </Button>
      </DialogActions>
    </>
  );
};
